import { useMemo } from 'react';
import { FileDown, FileSpreadsheet, Printer, Share2, Mail, Save, FolderOpen, Layers, RotateCcw, Loader2, History, CalendarClock, Bell } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { IndustryFilter } from '@/components/layout/IndustryFilter';
import { CompanyGroupFilters } from '@/components/layout/CompanyGroupFilters';
import { useFilters } from '@/contexts/FilterContext';
import { useRBAC } from '@/contexts/RBACContext';
import { INDUSTRY_TYPE_ORDER } from '@/lib/constants';
import type { IndustryType, Company } from '@/types';
import type { CompareSection, ReportViewMode } from '@/types/reports';
import { COMPARE_SECTIONS } from '@/types/reports';

interface ReportsStatusBarProps {
  onExportPdf?: () => void;
  onExportExcel?: () => void;
  isExporting?: boolean;
  onPrint?: () => void;
  onShare?: () => void;
  onEmail?: () => void;
  onSubscribe?: () => void;
  /** Why the current report can't be scheduled; Subscribe is disabled and shows it. */
  subscribeDisabledReason?: string | null;
  onManageSubscriptions?: () => void;
  // Compare mode props
  viewMode?: ReportViewMode;
  activeSections?: CompareSection[];
  onSectionsChange?: (sections: CompareSection[]) => void;
  // Data visibility props (internal users only)
  isInternalUser?: boolean;
  includeAllDeployments?: boolean;
  onIncludeAllDeploymentsChange?: (checked: boolean) => void;
}

export function ReportsStatusBar({
  onExportPdf,
  onExportExcel,
  isExporting = false,
  onPrint,
  onShare,
  onEmail,
  onSubscribe,
  subscribeDisabledReason = null,
  onManageSubscriptions,
  viewMode = 'charts',
  activeSections = ['info', 'kpis', 'chart', 'stats'],
  onSectionsChange,
  isInternalUser = false,
  includeAllDeployments = false,
  onIncludeAllDeploymentsChange,
}: ReportsStatusBarProps) {
  const {
    units,
    companies,
    groups,
    selectedCompanyIds,
    selectedGroupIds,
    selectedIndustryTypes,
    selectedModels,
    setSelectedIndustryTypes,
    setSelectedCompanyIds,
    setSelectedGroupIds,
    setSelectedModels,
  } = useFilters();

  // Get role information to control reset button visibility
  const { isSuperadmin, isAdmin, isCompanyAdmin } = useRBAC();
  const canResetFilters = isSuperadmin || isAdmin;
  // Subscription management is for company-wide admins (matches the backend
  // reportAdminScope gate, ISS-063) — hide the actions from a group_admin / plain
  // `user` so they don't hit a 403 dead-end.
  const canManageSubscriptions = isSuperadmin || isAdmin || isCompanyAdmin;

  // Calculate which industries actually have data (companies with that industry)
  // Validates against INDUSTRY_TYPE_ORDER to ensure type safety
  const industriesWithData = useMemo((): IndustryType[] => {
    const validIndustryTypes = new Set<string>(INDUSTRY_TYPE_ORDER);
    const industries = new Set<IndustryType>();
    companies.forEach((a: Company) => {
      if (a.industry && validIndustryTypes.has(a.industry)) {
        industries.add(a.industry as IndustryType);
      }
    });
    return Array.from(industries);
  }, [companies]);

  // Check if main filters are at default (all selected)
  const isFiltersDefault = useMemo(() => {
    // All industries with data are selected
    const allIndustriesSelected = industriesWithData.length > 0 &&
      industriesWithData.every(ind => selectedIndustryTypes.includes(ind));
    // All companies are selected
    const allCompaniesSelected = companies.length > 0 && selectedCompanyIds.length === companies.length;
    // All groups are selected
    const allGroupsSelected = groups.length > 0 && selectedGroupIds.length === groups.length;
    // All models are selected (3 model types)
    const allModelsSelected = selectedModels.length === 3;

    return allIndustriesSelected && allCompaniesSelected && allGroupsSelected && allModelsSelected;
  }, [industriesWithData, selectedIndustryTypes, companies, selectedCompanyIds, groups, selectedGroupIds, selectedModels]);

  // Reset main filters to default (select all)
  const handleResetFilters = () => {
    // Select all industries that have data (already validated as IndustryType[])
    setSelectedIndustryTypes(industriesWithData);
    // Select all companies
    setSelectedCompanyIds(companies.map((a: Company) => a.id));
    // Select all groups
    setSelectedGroupIds(groups.map(g => g.id));
    // Select all models
    setSelectedModels(['1RE', '2RE', 'WEA']);
  };

  return (
    <div className="h-12 bg-card border-b border-border flex items-center justify-between px-6 shrink-0 relative">
      {/* Left: Report CRUD (Future Phase) */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          disabled
          className="h-8 px-3 text-xs text-muted-foreground/50 gap-1.5"
          title="Load saved report (coming soon)"
        >
          <FolderOpen className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Load</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled
          className="h-8 px-3 text-xs text-muted-foreground/50 gap-1.5"
          title="Save report (coming soon)"
        >
          <Save className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Save</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled
          className="h-8 px-3 text-xs text-muted-foreground/50 gap-1.5"
          title="Report presets (coming soon)"
        >
          <Layers className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Presets</span>
        </Button>
      </div>

      {/* Center: Filter Group + Reset (absolutely centered) OR Section Selector in Compare mode */}
      <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
        {viewMode === 'compare' && onSectionsChange ? (
          /* Compare mode: Multi-select section toggles */
          <div className="flex items-center gap-3">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium">
              View Sections
            </span>
            <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1">
              {COMPARE_SECTIONS.map((section) => {
                const isSelected = activeSections.includes(section.value);
                return (
                  <button
                    key={section.value}
                    type="button"
                    onClick={() => {
                      if (isSelected) {
                        // Remove section (keep at least one)
                        if (activeSections.length > 1) {
                          onSectionsChange(activeSections.filter(s => s !== section.value));
                        }
                      } else {
                        // Add section maintaining order
                        const newSections = [...activeSections, section.value];
                        // Sort by COMPARE_SECTIONS order
                        const sorted = COMPARE_SECTIONS.filter(s => newSections.includes(s.value)).map(s => s.value);
                        onSectionsChange(sorted);
                      }
                    }}
                    className={cn(
                      "px-3 py-1.5 text-xs rounded-md transition-all duration-200 font-medium",
                      isSelected
                        ? "bg-[#8FB560] text-black shadow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    )}
                  >
                    {section.label}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          /* Charts/Summary mode: Filters */
          <>
            {/* Filter Group - GROUP & MODEL moved to sidebar, COMPANY stays here */}
            <div className="flex items-center gap-2">
              <IndustryFilter units={units} />
              <CompanyGroupFilters units={units} hideGroupFilter />
            </div>

            {/* Include All Deployments toggle - only visible to internal users */}
            {isInternalUser && onIncludeAllDeploymentsChange && (
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-border">
                <Checkbox
                  id="all-deployments"
                  checked={includeAllDeployments}
                  onCheckedChange={(checked) => onIncludeAllDeploymentsChange(checked === true)}
                  className="h-3.5 w-3.5"
                />
                <label
                  htmlFor="all-deployments"
                  className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors"
                  title="Show data from all deployment periods with visual annotations"
                >
                  <History className="h-3 w-3" />
                  <span>All Deployments</span>
                </label>
              </div>
            )}

            {/* Reset Button - only visible to superadmin and admin roles */}
            {canResetFilters && (
              <button
                onClick={handleResetFilters}
                disabled={isFiltersDefault}
                className="flex items-center text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Reset filters"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </button>
            )}
          </>
        )}
      </div>

      {/* Right: Action Buttons */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onExportExcel}
          disabled={isExporting}
          className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1.5"
          title="Export as Excel"
        >
          {isExporting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <FileSpreadsheet className="h-3.5 w-3.5" />
          )}
          <span className="hidden sm:inline">{isExporting ? 'Exporting...' : 'Excel'}</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onExportPdf}
          className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1.5"
          title="Export as PDF"
        >
          <FileDown className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">PDF</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrint}
          className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1.5"
          title="Print report"
        >
          <Printer className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Print</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onShare}
          className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1.5"
          title="Share report"
        >
          <Share2 className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Share</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={onEmail}
          className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1.5"
          title="Email report"
        >
          <Mail className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Email</span>
        </Button>
        {onSubscribe && canManageSubscriptions && (subscribeDisabledReason ? (
          // Unavailable: dimmed and aria-disabled, but still focusable and
          // tappable so the reason shows on touch and keyboard, not just hover.
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                aria-disabled="true"
                aria-label={`Subscribe (unavailable: ${subscribeDisabledReason})`}
                className="h-8 px-3 text-xs text-muted-foreground/50 gap-1.5 cursor-not-allowed"
              >
                <CalendarClock className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Subscribe</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 p-3 text-sm text-foreground">
              {subscribeDisabledReason}
            </PopoverContent>
          </Popover>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            onClick={onSubscribe}
            aria-label="Subscribe"
            title="Schedule this report by email"
            className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1.5"
          >
            <CalendarClock className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Subscribe</span>
          </Button>
        ))}
        {onManageSubscriptions && canManageSubscriptions && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onManageSubscriptions}
            className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground gap-1.5"
            title="Manage my report subscriptions"
          >
            <Bell className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Subscriptions</span>
          </Button>
        )}
      </div>
    </div>
  );
}
